import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "@/lib/api";

const KPI_STYLES = ["card-yellow", "card-pink", "card-blue", "card-green", "card-ochre"];
const AGENT_COLORS = ["#131E3A", "#25D366", "#E91E63", "#2196F3", "#FF9800", "#9C27B0"];
const fmtMins = (m) => (m == null ? "—" : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/analytics/agents").then((r) => setPerf(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-6 font-hand text-xl text-amber-800">crunching numbers...</div>;

  const kpis = [
    { label: "Messages Received", value: data.messages_in },
    { label: "Messages Sent", value: data.messages_out },
    { label: "Open Conversations", value: data.open_conversations },
    { label: "Active 24h Sessions", value: data.active_sessions_24h },
    { label: "Total Contacts", value: data.contacts },
  ];

  return (
    <div className="h-full overflow-y-auto p-6">
      <h1 className="font-heading text-2xl font-extrabold">Command Center</h1>
      <div className="font-hand text-lg text-amber-800 mb-5">the whole desk at a glance 📌</div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="kpi-cards">
        {kpis.map((k, i) => (
          <div key={k.label} className={`card-tactile ${KPI_STYLES[i]} p-4`}>
            <div className="label-upper">{k.label}</div>
            <div className="font-heading text-3xl font-extrabold">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-tactile p-5">
          <div className="font-heading font-bold mb-3">Message Volume (7 days)</div>
          <div className="h-64" style={{ minHeight: 256 }} data-testid="volume-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_volume}>
                <XAxis dataKey="day" tick={{ fontSize: 11, fontFamily: "DM Sans" }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "2px solid #131E3A", borderRadius: 8, fontFamily: "DM Sans", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="inbound" name="Received" fill="#25D366" radius={[4, 4, 0, 0]} />
                <Bar dataKey="outbound" name="Sent" fill="#131E3A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-tactile p-5">
          <div className="font-heading font-bold mb-3">Agent Performance (last 7 days)</div>
          <table className="w-full text-sm" data-testid="agent-perf-table">
            <thead>
              <tr className="text-left border-b-2" style={{ borderColor: "var(--cd-navy)" }}>
                <th className="py-2 font-heading">Agent</th>
                <th className="py-2 font-heading">Replies</th>
                <th className="py-2 font-heading">Chats</th>
                <th className="py-2 font-heading">Open</th>
                <th className="py-2 font-heading">Resolved</th>
                <th className="py-2 font-heading">Avg Resolution</th>
                <th className="py-2 font-heading">CSAT</th>
              </tr>
            </thead>
            <tbody>
              {(perf?.agents || []).map((a) => (
                <tr key={a.id} className="border-b" style={{ borderColor: "#E5DCB8" }} data-testid={`agent-perf-row-${a.name}`}>
                  <td className="py-2 font-semibold">{a.name} {a.role === "admin" && <span className="status-badge badge-pending ml-1">admin</span>}</td>
                  <td className="py-2">{a.replies}</td>
                  <td className="py-2">{a.chats_handled}</td>
                  <td className="py-2">{a.open_chats}</td>
                  <td className="py-2">{a.resolved}</td>
                  <td className="py-2 font-mono2 text-xs">{fmtMins(a.avg_resolution_minutes)}</td>
                  <td className="py-2 text-xs font-semibold" data-testid={`csat-cell-${a.name}`}>
                    {a.csat_avg != null ? <span className="text-amber-600">★ {a.csat_avg} <span className="text-slate-400 font-normal">({a.csat_count})</span></span> : "—"}
                  </td>
                </tr>
              ))}
              {(!perf || perf.agents.length === 0) && <tr><td colSpan={7} className="py-4 font-hand text-lg text-amber-800">no agents yet — add them in Settings</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-tactile p-5 mt-6">
        <div className="font-heading font-bold mb-3">Chats Handled Per Day · Per Agent</div>
        <div className="h-64" style={{ minHeight: 256 }} data-testid="agent-daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perf?.daily_chart || []}>
              <XAxis dataKey="day" tick={{ fontSize: 11, fontFamily: "DM Sans" }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "2px solid #131E3A", borderRadius: 8, fontFamily: "DM Sans", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {(perf?.agents || []).map((a, i) => (
                <Bar key={a.id} dataKey={a.name} fill={AGENT_COLORS[i % AGENT_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
